import type { WelcomePlaylistVideo } from '@/core/domain/entities/WelcomeVideo';
import type {
  QuickIntroState,
  QuickIntroVideoState,
} from '@/core/domain/repositories/IQuickIntroRepository';
import {
  welcomeCooldownMs,
  QUICK_INTRO_MAX_APPEARANCES,
} from '@/core/domain/entities/WelcomeVideoHistory';
import { getWelcomeVideoSource } from '@/shared/utils/welcomeVideoSource';

type StorageAccess = Pick<Storage, 'getItem' | 'setItem'>;
export type QuickIntroStorage = () => {
  local: StorageAccess;
  session: StorageAccess;
};
export type QuickIntroExclusive = <T>(
  operation: () => Promise<T>
) => Promise<T>;
export interface QuickIntroOutboxItem {
  id: string;
  action: 'activity' | 'shown' | 'cancel' | 'opt-out';
  body: Record<string, unknown>;
}

const GUEST_KEY = 'motito:quick-intro:guest:v1';
const DEVICE_OPENED_KEY = 'motito:quick-intro:device-opened:v1';
const OUTBOX_PREFIX = 'motito:quick-intro:outbox:v1:';
const SUPPRESSION_KEY = 'motito:welcome-video:suppressed:v1';
const VISIT_KEY = 'motito:welcome-video:visit:v1';
const LEGACY_PREFIX = 'motito:welcome-video:v2:';
const MAX_OUTBOX_ITEMS = 200;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function invalid(): never {
  throw new Error('Quick Intro history is unavailable or invalid.');
}
function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function text(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}
function timestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.exec(
      value
    );
  if (!match || !Number.isFinite(Date.parse(value)) || Date.parse(value) < 0)
    return false;
  const [year, month, day, hour, minute, second] = match
    .slice(1)
    .map(Number) as [number, number, number, number, number, number];
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= new Date(Date.UTC(year, month, 0)).getUTCDate() &&
    hour < 24 &&
    minute < 60 &&
    second < 60
  );
}
function exactKeys(
  value: Record<string, unknown>,
  required: string[],
  optional: string[] = []
): boolean {
  return (
    required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every(
      (key) => required.includes(key) || optional.includes(key)
    )
  );
}
function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
function appearances(value: unknown): value is number {
  return (
    Number.isInteger(value) &&
    (value as number) >= 0 &&
    (value as number) <= QUICK_INTRO_MAX_APPEARANCES
  );
}
function validateVideo(value: unknown): value is QuickIntroVideoState {
  if (
    !record(value) ||
    !exactKeys(
      value,
      [
        'video_id',
        'appearances',
        'watched',
        'retired',
        'progress',
        'played_versions',
      ],
      ['reserved_appearances']
    ) ||
    !text(value.video_id) ||
    !appearances(value.appearances) ||
    typeof value.watched !== 'boolean' ||
    typeof value.retired !== 'boolean' ||
    (Object.prototype.hasOwnProperty.call(value, 'reserved_appearances') &&
      !appearances(value.reserved_appearances)) ||
    !Array.isArray(value.progress) ||
    !Array.isArray(value.played_versions)
  )
    return false;
  const versions = new Set<string>();
  for (const progress of value.progress) {
    if (
      !record(progress) ||
      !exactKeys(progress, ['media_version', 'duration_seconds', 'ranges']) ||
      !text(progress.media_version) ||
      versions.has(progress.media_version) ||
      !finite(progress.duration_seconds) ||
      progress.duration_seconds <= 0 ||
      !Array.isArray(progress.ranges)
    )
      return false;
    versions.add(progress.media_version);
    for (const range of progress.ranges) {
      if (
        !Array.isArray(range) ||
        range.length !== 2 ||
        !finite(range[0]) ||
        !finite(range[1]) ||
        range[0] < 0 ||
        range[1] < range[0] ||
        range[1] > progress.duration_seconds
      )
        return false;
    }
  }
  return (
    value.played_versions.every(text) &&
    new Set(value.played_versions).size === value.played_versions.length
  );
}
function validateGuest(value: unknown): asserts value is QuickIntroState {
  if (
    !record(value) ||
    !exactKeys(value, [
      'account_id',
      'opted_out',
      'last_opened_at',
      'automatic_hold_until',
      'videos',
    ]) ||
    value.account_id !== null ||
    typeof value.opted_out !== 'boolean' ||
    (value.last_opened_at !== null && !timestamp(value.last_opened_at)) ||
    (value.automatic_hold_until !== null &&
      !timestamp(value.automatic_hold_until)) ||
    !Array.isArray(value.videos) ||
    !value.videos.every(validateVideo) ||
    new Set(value.videos.map((video) => video.video_id)).size !==
      value.videos.length
  )
    invalid();
}
function optedOut(local: StorageAccess): boolean {
  const value = local.getItem(SUPPRESSION_KEY);
  if (value !== null && value !== 'true' && value !== 'false') invalid();
  return value === 'true';
}
function legacyPreference(
  local: StorageAccess,
  alias: string
): { appearances: number; retired: boolean } | null {
  const source = getWelcomeVideoSource({
    enabled: true,
    video_url: alias,
    poster_url: null,
    captions_url: null,
  });
  if (!source) return null;
  const identity =
    source.provider === 'youtube'
      ? `youtube:${source.videoId}`
      : source.videoSrc;
  const key = `${LEGACY_PREFIX}${encodeURIComponent(identity)}`;
  const raw = local.getItem(key),
    completed = local.getItem(`${key}:completed`);
  if (completed !== null && completed !== 'true' && completed !== 'false')
    invalid();
  if (raw === null)
    return completed === 'true' ? { appearances: 0, retired: true } : null;
  const value: unknown = JSON.parse(raw);
  if (
    !record(value) ||
    !exactKeys(value, ['impressions']) ||
    !appearances(value.impressions)
  )
    invalid();
  return { appearances: value.impressions, retired: completed === 'true' };
}
function accountKey(accountId: string): string {
  if (!text(accountId)) invalid();
  return `${OUTBOX_PREFIX}${encodeURIComponent(accountId)}`;
}
function jsonValue(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object' || ancestors.has(value)) return false;
  ancestors.add(value);
  const valid = (Array.isArray(value) ? value : Object.values(value)).every(
    (item) => jsonValue(item, ancestors)
  );
  ancestors.delete(value);
  return (
    valid &&
    (Array.isArray(value) ||
      Object.getPrototypeOf(value) === Object.prototype ||
      Object.getPrototypeOf(value) === null)
  );
}
function validateOutboxItem(
  value: unknown,
  accountId: string
): asserts value is QuickIntroOutboxItem {
  if (
    !record(value) ||
    !exactKeys(value, ['id', 'action', 'body']) ||
    typeof value.id !== 'string' ||
    !UUID.test(value.id) ||
    !['activity', 'shown', 'cancel', 'opt-out'].includes(value.action as string) ||
    !record(value.body) ||
    value.body.expected_account_id !== accountId ||
    !jsonValue(value.body)
  )
    invalid();
  if (value.action === 'shown' || value.action === 'cancel') {
    if (!exactKeys(value.body as Record<string, unknown>, ['expected_account_id', 'token']) ||
      typeof (value.body as Record<string, unknown>).token !== 'string' ||
      !UUID.test((value.body as Record<string, unknown>).token as string)) invalid();
  }
}
const browserExclusive: QuickIntroExclusive = async (operation) => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request)
    throw new Error(
      'Quick Intro automatic invitations require browser locking.'
    );
  return navigator.locks.request('motito:quick-intro', operation);
};

export type QuickIntroPromptLock = () => Promise<(() => void) | null>;
const browserPromptLock: QuickIntroPromptLock = () => new Promise((resolve, reject) => {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    reject(new Error('Quick Intro automatic invitations require browser locking.'));
    return;
  }
  void navigator.locks.request('motito:quick-intro:invitation', { ifAvailable: true }, lock => {
    if (!lock) { resolve(null); return; }
    return new Promise<void>(release => { resolve(release); });
  }).catch(reject);
});

/** Caller holds exclusive() for read-modify-write; failures must suppress automatic admission. */
export class QuickIntroBrowserHistory {
  constructor(
    private readonly storage: QuickIntroStorage,
    private readonly lock: QuickIntroExclusive = browserExclusive,
    private readonly promptLock: QuickIntroPromptLock = browserPromptLock
  ) {}

  acquirePrompt(): Promise<(() => void) | null> { return this.promptLock(); }

  exclusive<T>(operation: () => Promise<T>): Promise<T> {
    return this.lock(operation);
  }

  readGuest(videos: WelcomePlaylistVideo[]): QuickIntroState {
    const { local } = this.storage();
    const raw = local.getItem(GUEST_KEY);
    let state: QuickIntroState = {
      account_id: null,
      opted_out: false,
      last_opened_at: null,
      automatic_hold_until: null,
      videos: [],
    };
    if (raw !== null) {
      const envelope: unknown = JSON.parse(raw);
      if (
        !record(envelope) ||
        !exactKeys(envelope, ['version', 'state']) ||
        envelope.version !== 1
      )
        invalid();
      validateGuest(envelope.state);
      state = envelope.state;
    }
    state.opted_out ||= optedOut(local);
    const byId = new Map(state.videos.map((video) => [video.video_id, video]));
    for (const video of videos) {
      for (const alias of video.legacy_sources) {
        const prior = legacyPreference(local, alias);
        if (!prior) continue;
        const current = byId.get(video.id) ?? {
          video_id: video.id,
          appearances: 0,
          watched: false,
          retired: false,
          progress: [],
          played_versions: [],
        };
        current.appearances = Math.max(current.appearances, prior.appearances);
        // Legacy ended events retire invitations, but never establish measured coverage.
        current.retired ||= prior.retired;
        byId.set(video.id, current);
      }
    }
    state.videos = [...byId.values()];
    return state;
  }

  saveGuest(state: QuickIntroState): void {
    validateGuest(state);
    const { local } = this.storage();
    const previous = this.readGuest([]);
    const suppressed = state.opted_out || previous.opted_out || optedOut(local);
    if (suppressed) local.setItem(SUPPRESSION_KEY, 'true');
    local.setItem(
      GUEST_KEY,
      JSON.stringify({ version: 1, state: { ...state, opted_out: suppressed } })
    );
  }

  deviceSuppressed(): boolean {
    const { local, session } = this.storage();
    return optedOut(local) || session.getItem(VISIT_KEY) !== null;
  }

  deviceBlocked(now: number, intervalHours?: unknown): boolean {
    if (!finite(now) || now < 0) invalid();
    if (this.deviceSuppressed()) return true;
    const { local } = this.storage();
    const previous = local.getItem(DEVICE_OPENED_KEY);
    if (previous === null) return false;
    if (!timestamp(previous)) invalid();
    return intervalHours !== 0 && now - Date.parse(previous) < welcomeCooldownMs(intervalHours);
  }

  markOpened(now: number): void {
    if (!finite(now) || now < 0) invalid();
    const { local, session } = this.storage();
    const previous = local.getItem(DEVICE_OPENED_KEY);
    if (previous !== null && !timestamp(previous)) invalid();
    local.setItem(
      DEVICE_OPENED_KEY,
      new Date(
        Math.max(now, previous === null ? 0 : Date.parse(previous))
      ).toISOString()
    );
    session.setItem(VISIT_KEY, 'shown');
  }

  suppress(): void {
    this.storage().local.setItem(SUPPRESSION_KEY, 'true');
  }

  readOutbox(accountId: string): QuickIntroOutboxItem[] {
    const raw = this.storage().local.getItem(accountKey(accountId));
    if (raw === null) return [];
    const envelope: unknown = JSON.parse(raw);
    if (
      !record(envelope) ||
      !exactKeys(envelope, ['version', 'items']) ||
      envelope.version !== 1 ||
      !Array.isArray(envelope.items) ||
      envelope.items.length > MAX_OUTBOX_ITEMS
    )
      invalid();
    const ids = new Set<string>();
    for (const item of envelope.items) {
      validateOutboxItem(item, accountId);
      if (ids.has(item.id)) invalid();
      ids.add(item.id);
    }
    return envelope.items;
  }

  appendOutbox(accountId: string, item: QuickIntroOutboxItem): void {
    validateOutboxItem(item, accountId);
    const items = this.readOutbox(accountId);
    const existing = items.find((entry) => entry.id === item.id);
    if (existing) {
      if (JSON.stringify(existing) !== JSON.stringify(item)) invalid();
      return;
    }
    if (items.length >= MAX_OUTBOX_ITEMS) invalid();
    this.storage().local.setItem(
      accountKey(accountId),
      JSON.stringify({ version: 1, items: [...items, item] })
    );
  }

  removeOutbox(accountId: string, id: string): void {
    const items = this.readOutbox(accountId).filter((item) => item.id !== id);
    this.storage().local.setItem(
      accountKey(accountId),
      JSON.stringify({ version: 1, items })
    );
  }
}
